import React, { useMemo } from 'react';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import Icon from '@material-ui/core/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Link from '@material-ui/core/Link';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { clsx } from '../../utils/htmlUtils';
// Components
import LockIcon from '../icons/iconLock';
import { TooltipButton } from '../tooltip-button';
import { CopyButton } from '../copy-button';
import ClickToCall from '../clickToCall';
// Ressources
import FormattingUtils from '../../utils/formattingUtils';
import StringUtils, { REGEX_LINK } from '../../utils/stringUtils';
import { Typobold } from '../typobold';
import { FormatType, ListCell } from '../../local-types';

// Constants
const COLUMN_DEFAULT = 2;
const MIN_DATA_LENGTH = 0;

// Types
type DataItemProps = {
    item: ListCell;
    index: number;
    nbColumn: number;
    t: (key: string) => string;
    classes: Record<string, string>;
};

// Utility function for link handling
const extractLinks = (value: string): { text: string; links: string[] } => {
    const links = [...(value.match(REGEX_LINK) || [])];
    const textWithoutLinks = links.reduce((acc, link) => acc.split(link).join(''), value);

    return {
        text: textWithoutLinks.trim(),
        links,
    };
};

// DataItem component
const DataItem = React.memo(({ item, index, nbColumn, t, classes }: DataItemProps) => {
    const { data, type, optional, customValue, customStyle, readOnly, emptyCell, hideCell, title, copyable } = item;

    const isEmpty = data == null || data.length === MIN_DATA_LENGTH;
    const isEmptyNotOptional = isEmpty && !optional;
    const formattedValue = FormattingUtils.format(data, type, t);

    const { text: valueWithoutLinks, links } = useMemo(
        () => (typeof formattedValue === 'string' ? extractLinks(formattedValue) : { text: formattedValue, links: [] }),
        [formattedValue],
    );

    // A cell can hold JSX (links, chips...), only a plain value can be copied
    const copyableValue =
        typeof formattedValue === 'string' || typeof formattedValue === 'number' ? formattedValue : null;

    if (hideCell) {
        return null;
    }
    if (emptyCell) {
        return <List key={index} className={classes.partnerInfoList} style={{ flex: `${100 / nbColumn}%` }} />;
    }

    return (
        <List key={index} className={classes.partnerInfoList} style={{ flex: `${100 / nbColumn}%` }}>
            <ListItem className={StringUtils.camelCase(title)}>
                <ListItemText
                    className={isEmptyNotOptional ? classes.partnerInfoMissing : classes.partnerInfoLabel}
                    primaryTypographyProps={{ className: classes.itemTypography }}
                >
                    <div className={classes.formLabel}>{title}</div>
                    {copyable && <CopyButton value={copyableValue} label={title} />}
                    {readOnly && (
                        <TooltipButton title={t('common:lock')}>
                            <LockIcon />
                        </TooltipButton>
                    )}
                </ListItemText>
                <ListItemText className={clsx(isEmptyNotOptional && classes.partnerInfoMissing, classes.data)}>
                    {!customValue && type === FormatType.Phone && <ClickToCall phoneNumber={formattedValue} />}
                    {!customValue && type !== FormatType.Phone && (
                        <div className={classes.value} style={{ ...customStyle, whiteSpace: 'pre-wrap' }}>
                            {valueWithoutLinks}
                            {links.map((link, i) => (
                                <Link
                                    key={`${link}-${i}`}
                                    href={link}
                                    target="_blank"
                                    color="secondary"
                                    rel="noreferrer noopener"
                                    className={classes.link}
                                >{`${t('common:link')} ${i}`}</Link>
                            ))}
                        </div>
                    )}
                    {customValue}
                </ListItemText>
            </ListItem>
        </List>
    );
});

// Types
interface ListItemProps extends WithTranslation {
    contentProps?: React.HTMLAttributes<HTMLDivElement>;
    nbColumn?: number;
    noCard?: boolean;
    title?: string;
    icon?: string;
    lock?: boolean;
    datas: ListCell[];
    cardActions?: React.ReactNode;
    loader?: React.ReactNode;
}

// Main component
const EnhancedListItem = React.memo((props: ListItemProps): JSX.Element => {
    const classes = useStyles();
    const { t, contentProps, nbColumn = COLUMN_DEFAULT, noCard, title, icon, lock, datas, cardActions, loader } = props;

    const renderContent = useMemo(
        () => (
            <div className={classes.cardContent}>
                {datas.map((item, index) => (
                    <DataItem
                        key={`${item.title}-${index}`}
                        item={item}
                        index={index}
                        nbColumn={nbColumn}
                        t={t}
                        classes={classes}
                    />
                ))}
                {loader}
            </div>
        ),
        [datas, nbColumn, loader, t, classes],
    );

    return (
        <div {...contentProps} className={classes.rootListItem}>
            <div className={classes.itemHeader}>
                {title && <Typobold>{title}</Typobold>}
                {icon && (
                    <Icon color="primary" className={classes.icon}>
                        {icon}
                    </Icon>
                )}
                {lock && (
                    <TooltipButton title={t('common:lock')}>
                        <Icon color="primary" className={classes.icon}>
                            lock
                        </Icon>
                    </TooltipButton>
                )}
            </div>

            {noCard ? (
                renderContent
            ) : (
                <Card className={classes.card}>
                    {renderContent}
                    {cardActions && <CardActions className={classes.rightActions}>{cardActions}</CardActions>}
                </Card>
            )}
        </div>
    );
});

EnhancedListItem.displayName = 'EnhancedListItem';
DataItem.displayName = 'DataItem';

//=====================================//
//=============== Style ===============//
//=====================================//

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootListItem: {
            width: '100%',
            alignItems: 'none',
            marginTop: theme.spacing(3),
            '& .MuiFormControl-root.MuiTextField-root': {
                '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl[data-shrink=false]': {
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
        },
        card: {
            padding: 16,
        },
        cardContent: {
            display: 'flex',
            flexWrap: 'wrap',
            padding: 0,
        },
        formLabel: {
            fontSize: 12,
            color: theme.palette.primary.dark,
            fontWeight: 500,
        },
        data: {
            paddingBottom: 8,
            paddingTop: 8,
            borderBottom: '1px solid #eee',
        },
        value: {
            fontSize: 16,
            fontWeight: 400,
            color: theme.palette.colors.table.text,
        },
        link: {
            marginRight: 8,
        },
        icon: {
            marginLeft: 8,
        },
        warning: {
            color: theme.palette.danger.contrastText,
        },
        partnerInfoList: {
            display: 'flex',
            '& li ': {
                flex: '1 0 50%',
                flexDirection: 'column',
                alignItems: 'inherit',
            },
            '& .MuiListItemText-root': {
                flex: 'initial',
                margin: theme.spacing(0, 1),
                paddingTop: 0,
            },
        },
        [theme.breakpoints.down('sm')]: {
            partnerInfoList: {
                flex: '51%',
                '& li ': {
                    flex: '1 0 51%',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'start',
                },
            },
        },
        partnerInfoLabel: {
            color: theme.palette.secondary.light,
        },
        partnerInfoMissing: {
            color: theme.palette.danger.contrastText,
        },
        rightActions: {
            justifyContent: 'flex-end',
        },
        itemTypography: {
            display: 'flex',
            alignItems: 'center',
        },
        itemHeader: {
            display: 'flex',
            alignItems: 'center',
            marginBottom: 20,
        },
    }),
);

export default withTranslation(['common'])(EnhancedListItem);
